import { NextRequest, NextResponse } from 'next/server';
import { getApiUser } from '@/lib/firebase/admin';
import { hasAdminAccess, isSuperAdmin } from '@/lib/serverPermissions';
import type { User } from '@/lib/types';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export interface ApiUser extends User {
  // Add any additional API-specific user properties if needed
  apiAccessLevel?: string;
}

export interface ApiContext {
  user: ApiUser;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  searchParams: URLSearchParams;
  requestId: string;
  startTime: number;
  clientIP: string;
  userAgent: string;
}

export interface ApiError {
  error: string;
  details?: unknown;
  requestId?: string;
  timestamp?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page?: number;
  limit?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  totalPages?: number;
}

export interface ApiPerformanceMetrics {
  requestId: string;
  method: string;
  path: string;
  duration: number;
  statusCode: number;
  userAgent: string;
  clientIP: string;
  userId?: string;
  timestamp: number;
}

// React 19: Request tracking interface
export interface ApiRequestTracker {
  id: string;
  method: string;
  path: string;
  startTime: number;
  endTime?: number;
  statusCode?: number;
  error?: string;
  userId?: string;
  clientIP: string;
}

// =============================================================================
// PERFORMANCE MONITORING
// =============================================================================

class ApiPerformanceMonitor {
  private static instance: ApiPerformanceMonitor;
  private metrics: Map<string, ApiPerformanceMetrics> = new Map();
  private activeRequests: Map<string, ApiRequestTracker> = new Map();
  private readonly MAX_METRICS_HISTORY = 1000;

  static getInstance(): ApiPerformanceMonitor {
    if (!ApiPerformanceMonitor.instance) {
      ApiPerformanceMonitor.instance = new ApiPerformanceMonitor();
    }
    return ApiPerformanceMonitor.instance;
  }

  startRequest(request: NextRequest): string {
    const requestId = `api-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const url = new URL(request.url);
    
    const tracker: ApiRequestTracker = {
      id: requestId,
      method: request.method,
      path: url.pathname,
      startTime: performance.now(),
      clientIP: this.getClientIP(request),
    };

    this.activeRequests.set(requestId, tracker);
    return requestId;
  }

  endRequest(requestId: string, statusCode: number, userId?: string, error?: string): void {
    const tracker = this.activeRequests.get(requestId);
    if (!tracker) return;

    tracker.endTime = performance.now();
    tracker.statusCode = statusCode;
    tracker.userId = userId;
    tracker.error = error;

    const duration = tracker.endTime - tracker.startTime;

    // Store metrics
    const metrics: ApiPerformanceMetrics = {
      requestId,
      method: tracker.method,
      path: tracker.path,
      duration,
      statusCode,
      userAgent: 'unknown', // Will be set by context
      clientIP: tracker.clientIP,
      userId,
      timestamp: Date.now(),
    };

    this.metrics.set(requestId, metrics);
    this.activeRequests.delete(requestId);

    // Cleanup old metrics
    if (this.metrics.size > this.MAX_METRICS_HISTORY) {
      const oldestKey = this.metrics.keys().next().value;
      if (oldestKey !== undefined) {
        this.metrics.delete(oldestKey);
      }
    }

    // Log slow requests
    if (duration > 1000) {
      console.warn(`[API] Slow request detected: ${tracker.method} ${tracker.path} took ${duration.toFixed(2)}ms`);
    }
  }

  getMetrics(): ApiPerformanceMetrics[] {
    return Array.from(this.metrics.values());
  }

  getActiveRequestsCount(): number {
    return this.activeRequests.size;
  }

  private getClientIP(request: NextRequest): string {
    const xForwardedFor = request.headers.get('x-forwarded-for');
    const xRealIP = request.headers.get('x-real-ip');
    const remoteAddr = request.headers.get('remote-addr');
    
    if (xForwardedFor) {
      return xForwardedFor.split(',')[0].trim();
    }
    
    return xRealIP || remoteAddr || 'unknown';
  }
}

// React 19: Global performance monitor instance
const performanceMonitor = ApiPerformanceMonitor.getInstance();

/**
 * React 19: Enhanced API error responses with tracking
 */
export const ApiErrors = {
  unauthorized: (message = 'Unauthorized', requestId?: string) => 
    NextResponse.json({ 
      error: message, 
      requestId, 
      timestamp: Date.now() 
    }, { status: 401 }),
  
  forbidden: (message = 'Forbidden', requestId?: string) => 
    NextResponse.json({ 
      error: message, 
      requestId, 
      timestamp: Date.now() 
    }, { status: 403 }),
  
  badRequest: (message: string, details?: unknown, requestId?: string) => 
    NextResponse.json({ 
      error: message, 
      details, 
      requestId, 
      timestamp: Date.now() 
    }, { status: 400 }),
  
  notFound: (message = 'Resource not found', requestId?: string) => 
    NextResponse.json({ 
      error: message, 
      requestId, 
      timestamp: Date.now() 
    }, { status: 404 }),
  
  conflict: (message: string, details?: unknown, requestId?: string) => 
    NextResponse.json({ 
      error: message, 
      details, 
      requestId, 
      timestamp: Date.now() 
    }, { status: 409 }),
  
  internal: (message = 'Internal server error', requestId?: string) => 
    NextResponse.json({ 
      error: message, 
      requestId, 
      timestamp: Date.now() 
    }, { status: 500 }),
} as const;

/**
 * React 19: Enhanced response builder with performance metadata
 */
export const ResponseBuilder = {
  success: <T>(data: T, message?: string, requestId?: string) => 
    NextResponse.json({
      success: true,
      data,
      message,
      requestId,
      timestamp: Date.now(),
    }),

  created: <T>(data: T, message?: string, requestId?: string) => 
    NextResponse.json({
      success: true,
      data,
      message,
      requestId,
      timestamp: Date.now(),
    }, { status: 201 }),

  list: <T>(items: T[], total?: number, requestId?: string) => 
    NextResponse.json({
      success: true,
      data: items,
      total: total ?? items.length,
      requestId,
      timestamp: Date.now(),
    }),

  paginated: <T>(response: PaginatedResponse<T>, requestId?: string) => {
    const totalPages = response.limit ? Math.ceil(response.total / response.limit) : 1;
    const currentPage = response.page || 1;
    
    return NextResponse.json({
      success: true,
      data: response.data,
      pagination: {
        total: response.total,
        page: currentPage,
        limit: response.limit,
        totalPages,
        hasNextPage: currentPage < totalPages,
        hasPreviousPage: currentPage > 1,
      },
      requestId,
      timestamp: Date.now(),
    });
  },
};

/**
 * React 19: Enhanced API context extraction with performance tracking
 */
export async function getApiContext(request: NextRequest): Promise<ApiContext | NextResponse> {
  const requestId = performanceMonitor.startRequest(request);
  
  try {
    const user = await getApiUser(request);
    if (!user) {
      performanceMonitor.endRequest(requestId, 401, undefined, 'Unauthorized');
      return ApiErrors.unauthorized('Authentication required', requestId);
    }

    const { searchParams } = new URL(request.url);
    const clientIP = request.headers.get('x-forwarded-for')?.split(',')[0].trim() || 
                     request.headers.get('x-real-ip') || 
                     request.headers.get('remote-addr') || 
                     'unknown';
    const userAgent = request.headers.get('user-agent') || 'unknown';
    
    return {
      user: user as ApiUser,
      isSuperAdmin: isSuperAdmin(user),
      isAdmin: hasAdminAccess(user),
      searchParams,
      requestId,
      startTime: performance.now(),
      clientIP,
      userAgent,
    };
  } catch (error) {
    console.error('Error getting API context:', error);
    performanceMonitor.endRequest(requestId, 500, undefined, 'Context extraction failed');
    return ApiErrors.internal('Authentication error', requestId);
  }
}

/**
 * Require admin access
 */
export function requireAdmin(context: ApiContext): NextResponse | null {
  if (!context.isAdmin) {
    return ApiErrors.forbidden('Admin access required');
  }
  return null;
}

/**
 * Require super admin access
 */
export function requireSuperAdmin(context: ApiContext): NextResponse | null {
  if (!context.isSuperAdmin) {
    return ApiErrors.forbidden('Super admin access required');
  }
  return null;
}

/**
 * Generic filter builder for arrays
 */
export class FilterBuilder<T> {
  private data: T[];
  
  constructor(data: T[]) {
    this.data = [...data];
  }

  where<K extends keyof T>(field: K, value: T[K] | null | undefined): FilterBuilder<T> {
    if (value != null) {
      this.data = this.data.filter(item => item[field] === value);
    }
    return this;
  }

  whereIn<K extends keyof T>(field: K, values: T[K][] | null | undefined): FilterBuilder<T> {
    if (values && values.length > 0) {
      this.data = this.data.filter(item => values.includes(item[field]));
    }
    return this;
  }

  whereIncludes<K extends keyof T>(field: K, value: string | null | undefined): FilterBuilder<T> {
    if (value && typeof this.data[0]?.[field] === 'string') {
      const searchLower = value.toLowerCase();
      this.data = this.data.filter(item => 
        (item[field] as unknown as string).toLowerCase().includes(searchLower)
      );
    }
    return this;
  }

  search(searchTerm: string | null | undefined, fields: (keyof T)[]): FilterBuilder<T> {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      this.data = this.data.filter(item =>
        fields.some(field => {
          const value = item[field];
          if (typeof value === 'string') {
            return value.toLowerCase().includes(searchLower);
          }
          return false;
        })
      );
    }
    return this;
  }

  custom(predicate: (item: T) => boolean): FilterBuilder<T> {
    this.data = this.data.filter(predicate);
    return this;
  }

  build(): T[] {
    return this.data;
  }

  buildPaginated(page?: number, limit?: number): PaginatedResponse<T> {
    const total = this.data.length;
    
    if (page && limit) {
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedData = this.data.slice(startIndex, endIndex);
      
      return {
        data: paginatedData,
        total,
        page,
        limit,
      };
    }
    
    return {
      data: this.data,
      total,
    };
  }
}

/**
 * Extract query parameters with type safety
 */
export class QueryExtractor {
  constructor(private searchParams: URLSearchParams) {}

  getString(key: string): string | null {
    return this.searchParams.get(key);
  }

  getNumber(key: string): number | null {
    const value = this.searchParams.get(key);
    if (!value) return null;
    const num = parseInt(value, 10);
    return isNaN(num) ? null : num;
  }

  getBoolean(key: string): boolean | null {
    const value = this.searchParams.get(key);
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  }

  getArray(key: string, separator = ','): string[] | null {
    const value = this.searchParams.get(key);
    return value ? value.split(separator).filter(Boolean) : null;
  }
}

/**
 * Standard validation helpers
 */
export const Validators = {
  required: (value: unknown, fieldName: string): string | null => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return `${fieldName} is required`;
    }
    return null;
  },

  email: (email: string): string | null => {
    // Simple email validation without external dependency
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? null : 'Invalid email format';
  },

  minLength: (value: string, min: number, fieldName: string): string | null => {
    if (value.length < min) {
      return `${fieldName} must be at least ${min} characters long`;
    }
    return null;
  },

  maxLength: (value: string, max: number, fieldName: string): string | null => {
    if (value.length > max) {
      return `${fieldName} must be no more than ${max} characters long`;
    }
    return null;
  },
} as const;

/**
 * Validation result builder
 */
export class ValidationBuilder {
  private errors: string[] = [];

  validate(validator: () => string | null): ValidationBuilder {
    const error = validator();
    if (error) {
      this.errors.push(error);
    }
    return this;
  }

  validateField<T>(value: T, fieldName: string, ...validators: Array<(val: T, name: string) => string | null>): ValidationBuilder {
    validators.forEach(validator => {
      const error = validator(value, fieldName);
      if (error) {
        this.errors.push(error);
      }
    });
    return this;
  }

  isValid(): boolean {
    return this.errors.length === 0;
  }

  getErrors(): string[] {
    return [...this.errors];
  }

  getFirstError(): string | null {
    return this.errors[0] || null;
  }
}

/**
 * Wrapper for handling API route logic with standard patterns
 */
export async function handleApiRoute<T extends object>(
  request: NextRequest,
  handler: (context: ApiContext) => Promise<T | NextResponse>
): Promise<NextResponse> {
  // React 19: Start performance monitoring
  const requestId = performanceMonitor.startRequest(request);
  
  try {
    const context = await getApiContext(request);
    
    // If context is a NextResponse (error), return it
    if ('status' in context) {
      performanceMonitor.endRequest(requestId, context.status, undefined, 'Context error');
      return context;
    }

    const result = await handler(context);
    
    // If result is already a NextResponse, return it
    if (result && typeof result === 'object' && 'status' in result) {
      const response = result as NextResponse;
      performanceMonitor.endRequest(requestId, response.status, context.user?.uid || undefined);
      return response;
    }

    // Otherwise, wrap result in success response with request tracking
    const successResponse = NextResponse.json({
      ...result,
      requestId,
      timestamp: Date.now(),
    });
    
    performanceMonitor.endRequest(requestId, 200, context.user?.uid);
    return successResponse;
  } catch (error) {
    console.error('API route error:', error);
    performanceMonitor.endRequest(requestId, 500, undefined, error instanceof Error ? error.message : 'Unknown error');
    return ApiErrors.internal('Internal server error', requestId);
  }
}

/**
 * Enhanced permission helpers for common use cases
 */
export const PermissionHelpers = {
  /**
   * Check if user can access resource by organizational boundaries
   */
  canAccessByOrganization: (context: ApiContext, resourceOrgId: string): boolean => {
    return context.isSuperAdmin || context.user.organizationId === resourceOrgId;
  },

  /**
   * Check if user can access resource by club association
   */
  canAccessByClub: (context: ApiContext, clubId: string): boolean => {
    return (
      context.isSuperAdmin ||
      (context.isAdmin && !!context.user.organizationId) ||
      (context.user.associatedClubIds?.includes(clubId) ?? false)
    );
  },

  /**
   * Check if user can modify user roles
   */
  canAssignRole: (context: ApiContext, targetRole: string): boolean => {
    if (targetRole === 'Super Admin') return context.isSuperAdmin;
    if (targetRole === 'Admin') return context.isAdmin || context.isSuperAdmin;
    return context.isAdmin || context.isSuperAdmin;
  },

  /**
   * Get accessible organization IDs for current user
   */
  getAccessibleOrganizationIds: (context: ApiContext): string[] => {
    if (context.isSuperAdmin) return []; // Empty array means all organizations
    return [context.user.organizationId];
  },

  /**
   * Get accessible club IDs for current user
   */
  getAccessibleClubIds: (context: ApiContext): string[] => {
    if (context.isSuperAdmin) return []; // Empty array means all clubs
    if (context.isAdmin) return []; // Admin can access all clubs in their org
    return context.user.associatedClubIds || [];
  }
};

/**
 * Standard resource ownership/access validation
 */
export const ResourceAccess = {
  /**
   * Validate user can access a resource based on ownership rules
   */
  validateAccess: (
    context: ApiContext,
    resource: { organizationId?: string; clubId?: string; ownerId?: string },
    options: { allowSelfAccess?: boolean; requireAdmin?: boolean } = {}
  ): NextResponse | null => {
    const { allowSelfAccess = false, requireAdmin = false } = options;

    // Super admin can access everything
    if (context.isSuperAdmin) return null;

    // Check admin requirement
    if (requireAdmin && !context.isAdmin) {
      return ApiErrors.forbidden('Admin access required');
    }

    // Check self-access (for user profiles, etc.)
    if (allowSelfAccess && resource.ownerId === context.user.uid) {
      return null;
    }

    // Check organizational access
    if (resource.organizationId && !PermissionHelpers.canAccessByOrganization(context, resource.organizationId)) {
      return ApiErrors.forbidden('Access denied - organization boundary violation');
    }

    // Check club access
    if (resource.clubId && !PermissionHelpers.canAccessByClub(context, resource.clubId)) {
      return ApiErrors.forbidden('Access denied - club access violation');
    }

    return null;
  }
};

/**
 * Enhanced validation with common patterns
 */
export const CommonValidators = {
  userRoles: (roles: string[]): string | null => {
    if (!Array.isArray(roles) || roles.length === 0) {
      return 'At least one role is required';
    }
    const validRoles = ['Admin', 'Staff', 'Tennis Club', 'Member', 'Teacher', 'Tennis School', 'Guest'];
    const invalidRoles = roles.filter(role => !validRoles.includes(role));
    if (invalidRoles.length > 0) {
      return `Invalid roles: ${invalidRoles.join(', ')}`;
    }
    return null;
  },

  organizationName: (name: string): string | null => {
    if (!name || name.trim() === '') {
      return 'Organization name is required';
    }
    if (name.length > 100) {
      return 'Organization name must be less than 100 characters';
    }
    return null;
  },

  status: (status: string, validStatuses: string[]): string | null => {
    if (!validStatuses.includes(status)) {
      return `Status must be one of: ${validStatuses.join(', ')}`;
    }
    return null;
  }
};

/**
 * Enhanced filtering with permission awareness
 */
export class PermissionAwareFilterBuilder<T> extends FilterBuilder<T> {
  constructor(data: T[], private context: ApiContext) {
    super(data);
  }

  /**
   * Filter by organization permissions
   */
  byOrganizationAccess(orgIdField: keyof T = 'organizationId' as keyof T): this {
    if (!this.context.isSuperAdmin) {
      this.where(orgIdField, this.context.user.organizationId as T[keyof T]);
    }
    return this;
  }

  /**
   * Filter by club permissions
   */
  byClubAccess(clubIdField: keyof T = 'clubId' as keyof T): this {
    if (!this.context.isSuperAdmin && !this.context.isAdmin) {
      const accessibleClubIds = PermissionHelpers.getAccessibleClubIds(this.context);
      if (accessibleClubIds.length > 0) {
        this.custom(item => accessibleClubIds.includes(item[clubIdField] as string));
      }
    }
    return this;
  }

  /**
   * Apply role-based visibility filters
   */
  byRoleVisibility(ownerIdField?: keyof T): this {
    if (!this.context.isSuperAdmin && !this.context.isAdmin && ownerIdField) {
      // Non-admin users can only see their own resources
      this.where(ownerIdField, this.context.user.uid as T[keyof T]);
    }
    return this;
  }
}

/**
 * Create permission-aware filter builder
 */
export function createPermissionAwareFilter<T>(data: T[], context: ApiContext): PermissionAwareFilterBuilder<T> {
  return new PermissionAwareFilterBuilder(data, context);
}

/**
 * Standard CRUD operation handlers
 */
export const CrudHandlers = {
  /**
   * Standard GET list handler with filtering and permissions
   */
  getList: <T>(
    data: T[],
    context: ApiContext,
    filterConfig?: {
      orgIdField?: keyof T;
      clubIdField?: keyof T;
      ownerIdField?: keyof T;
      searchFields?: (keyof T)[];
    }
  ) => {
    const query = new QueryExtractor(context.searchParams);
    const search = query.getString('search');
    
    const builder = createPermissionAwareFilter(data, context);
    
    if (filterConfig?.orgIdField) {
      builder.byOrganizationAccess(filterConfig.orgIdField);
    }
    
    if (filterConfig?.clubIdField) {
      builder.byClubAccess(filterConfig.clubIdField);
    }
    
    if (filterConfig?.ownerIdField) {
      builder.byRoleVisibility(filterConfig.ownerIdField);
    }
    
    if (search && filterConfig?.searchFields) {
      builder.search(search, filterConfig.searchFields);
    }
    
    const items = builder.build();
    return ResponseBuilder.list(items);
  }
};

/**
 * File Upload Security Helpers
 */
export const FileUploadHelpers = {
  /**
   * Validate file type against allowed types
   */
  validateFileType: (file: File, allowedTypes: string[]): string | null => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedTypes.includes(fileExtension)) {
      return `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`;
    }
    return null;
  },

  /**
   * Validate file size
   */
  validateFileSize: (file: File, maxSizeBytes: number): string | null => {
    if (file.size > maxSizeBytes) {
      const maxSizeMB = Math.round(maxSizeBytes / (1024 * 1024));
      return `File size too large. Maximum allowed: ${maxSizeMB}MB`;
    }
    return null;
  },

  /**
   * Sanitize filename to prevent path traversal
   */
  sanitizeFileName: (fileName: string): string => {
    return fileName
      .replace(/[^a-zA-Z0-9.-]/g, '_')
      .replace(/\.{2,}/g, '.')
      .toLowerCase();
  },

  /**
   * Generate secure filename with timestamp
   */
  generateSecureFileName: (userId: string, originalName: string, prefix = ''): string => {
    const sanitized = FileUploadHelpers.sanitizeFileName(originalName);
    const extension = sanitized.split('.').pop() || 'jpg';
    const timestamp = Date.now();
    return `${prefix}${userId}_${timestamp}.${extension}`;
  },

  /**
   * Validate upload permissions for user
   */
  validateUploadPermissions: (
    context: ApiContext, 
    targetUserId: string, 
    uploadType: 'profile' | 'organizationLogo'
  ): NextResponse | null => {
    // Users can upload their own profile pictures
    if (uploadType === 'profile' && context.user.uid === targetUserId) {
      return null;
    }

    // Admins can upload for users in their organization
    if (uploadType === 'profile' && context.isAdmin) {
      // Additional check needed: verify target user is in same organization
      return null; // Will be validated in the route
    }

    // Only admins can upload organization logos
    if (uploadType === 'organizationLogo' && !context.isAdmin) {
      return ApiErrors.forbidden('Admin access required for organization logo uploads');
    }

    // Super admins can upload anything
    if (context.isSuperAdmin) {
      return null;
    }

    return ApiErrors.forbidden('Insufficient permissions for this upload type');
  }
};

/**
 * Caching Helpers
 */
export const CacheHelpers = {
  /**
   * Set cache headers on response
   */
  setCacheHeaders: (response: NextResponse, ttlSeconds: number): NextResponse => {
    response.headers.set('Cache-Control', `public, max-age=${ttlSeconds}`);
    response.headers.set('Expires', new Date(Date.now() + ttlSeconds * 1000).toUTCString());
    return response;
  },

  /**
   * Create cache key for request
   */
  createCacheKey: (prefix: string, params: Record<string, string | null>): string => {
    const sortedParams = Object.entries(params)
      .filter(([_, value]) => value !== null)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}:${value}`)
      .join('|');
    return `${prefix}:${sortedParams}`;
  },

  /**
   * Get cache-friendly response with etag
   */
  withETag: (data: unknown): { data: unknown; etag: string } => {
    const etag = `"${Buffer.from(JSON.stringify(data)).toString('base64').slice(0, 16)}"`;
    return { data, etag };
  }
};

/**
 * Audit Logging Helpers
 */
export const AuditHelpers = {
  /**
   * Log API access events
   */
  logApiAccess: (
    _context: ApiContext, 
    action: string, 
    resource?: string, 
    _metadata?: Record<string, unknown>
  ): void => {
    // Minimal logging - only action and resource
    console.info(`🔍 ${action}${resource ? ` ${resource}` : ''}`);
  },

  /**
   * Log data modification events
   */
  logDataChange: (
    _context: ApiContext,
    operation: 'CREATE' | 'UPDATE' | 'DELETE',
    resourceType: string,
    _resourceId: string,
    _changes?: Record<string, { old?: unknown; new?: unknown }>
  ): void => {
    // Minimal logging - only operation and resource type
    console.info(`📝 ${operation} ${resourceType}`);
  },

  /**
   * Log security events
   */
  logSecurityEvent: (
    context: ApiContext | null,
    event: 'UNAUTHORIZED_ACCESS' | 'PERMISSION_DENIED' | 'SUSPICIOUS_ACTIVITY' | 'FILE_UPLOAD',
    details: Record<string, unknown>
  ): void => {
    console.warn('🚨 Security Event', {
      timestamp: new Date().toISOString(),
      userId: context?.user.uid || 'anonymous',
      userEmail: context?.user.email || 'unknown',
      organizationId: context?.user.organizationId || 'unknown',
      event,
      details
    });
  }
};

/**
 * Enhanced file upload validation with security
 */
export class FileUploadValidator {
  private errors: string[] = [];

  constructor(private context: ApiContext) {}

  validateFile(
    file: File, 
    options: {
      maxSizeBytes?: number;
      allowedTypes?: string[];
      required?: boolean;
    } = {}
  ): this {
    const { maxSizeBytes = 5 * 1024 * 1024, allowedTypes = ['jpg', 'jpeg', 'png'], required = true } = options;

    if (!file && required) {
      this.errors.push('File is required');
      return this;
    }

    if (!file) return this;

    const typeError = FileUploadHelpers.validateFileType(file, allowedTypes);
    if (typeError) this.errors.push(typeError);

    const sizeError = FileUploadHelpers.validateFileSize(file, maxSizeBytes);
    if (sizeError) this.errors.push(sizeError);

    return this;
  }

  validateUploadType(uploadType: string, allowedTypes: string[]): this {
    if (!allowedTypes.includes(uploadType)) {
      this.errors.push(`Invalid upload type. Allowed: ${allowedTypes.join(', ')}`);
    }
    return this;
  }

  validateTargetUser(targetUserId: string): this {
    if (!targetUserId || typeof targetUserId !== 'string') {
      this.errors.push('Valid target user ID is required');
    }
    return this;
  }

  isValid(): boolean {
    return this.errors.length === 0;
  }

  getErrors(): string[] {
    return [...this.errors];
  }

  getFirstError(): string | null {
    return this.errors[0] || null;
  }
}

// =============================================================================
// SERVER ACTION UTILITIES (React 19 Enhanced)
// =============================================================================

/**
 * Server action state interface
 */
export interface ServerActionState<T = unknown> {
  data?: T;
  error?: string;
  success?: boolean;
  pending?: boolean;
}

/**
 * React 19: Create server action with proper error handling
 * For use with React 19's useActionState hook
 */
export function createServerAction<TData extends Record<string, unknown>, TResult>(
  serverActionFn: (data: TData) => Promise<TResult>,
  validator?: (data: TData) => string | null
) {
  return async (prevState: ServerActionState<TResult>, formData: TData): Promise<ServerActionState<TResult>> => {
    // Validate if validator provided
    if (validator) {
      const validationError = validator(formData);
      if (validationError) {
        return {
          success: false,
          error: validationError,
          pending: false
        };
      }
    }

    try {
      const result = await serverActionFn(formData);
      return {
        success: true,
        data: result,
        error: undefined,
        pending: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Server action failed',
        pending: false
      };
    }
  };
}



/**
 * Enhanced server action with API context validation
 */
export function createApiServerAction<TData extends Record<string, unknown>, TResult>(
  actionFn: (data: TData, context: ApiContext) => Promise<TResult>,
  options: {
    requireAdmin?: boolean;
    requireSuperAdmin?: boolean;
    validator?: (data: TData) => string | null;
  } = {}
) {
  return async (prevState: ServerActionState<TResult>, formData: TData): Promise<ServerActionState<TResult>> => {
    try {
      // Note: In a real implementation, you would need to get the API context
      // This is a simplified version for demonstration
      
      // Validate if validator provided
      if (options.validator) {
        const validationError = options.validator(formData);
        if (validationError) {
          return {
            success: false,
            error: validationError,
            pending: false
          };
        }
      }

      // Note: Permission checks would be implemented here with actual context
      // For now, this is a placeholder structure
      
      const result = await actionFn(formData, {} as ApiContext);
      return {
        success: true,
        data: result,
        error: undefined,
        pending: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'API server action failed',
        pending: false
      };
    }
  };
} 
